import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtTL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import ClientFormDialog from "@/components/ClientFormDialog";
import { toast } from "sonner";
import { Plus, Search, Building2, MoreHorizontal, Pencil, Trash2, Printer, UserCheck, X } from "lucide-react";
import { useStaffNames, rememberStaff } from "@/lib/staff";

const TURLER = ["Şahıs", "Limited", "Anonim", "Diğer"];

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [tur, setTur] = useState("hepsi");
  const [open, setOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);
  const staffNames = useStaffNames();
  const nav = useNavigate();
  const [sp] = useSearchParams();

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (tur !== "hepsi") params.tur = tur;
    api.get("/clients", { params }).then((r) => setClients(r.data));
  };
  useEffect(() => { load(); }, [q, tur]);
  useEffect(() => { if (sp.get("yeni")) { setEditClient(null); setOpen(true); } }, [sp]);

  const openNew = () => { setEditClient(null); setOpen(true); };
  const openEdit = (c) => { setEditClient(c); setOpen(true); };

  const printList = async () => {
    const params = {};
    if (q) params.q = q;
    if (tur !== "hepsi") params.tur = tur;
    try {
      const res = await api.get("/clients/export-xlsx", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = "mukellef_listesi.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Mükellef listesi indirildi");
    } catch (e) { toast.error("Liste oluşturulamadı"); }
  };

  const allSelected = clients.length > 0 && clients.every((c) => selected.has(c.id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(clients.map((c) => c.id)));

  const bulkAssign = async () => {
    const name = assignTo.trim();
    if (!name) return toast.error("Personel adını yazın veya listeden seçin");
    if (!window.confirm(`${selected.size} mükellefin sorumlu personeli "${name}" olarak değiştirilsin mi?`)) return;
    setAssigning(true);
    try {
      const { data } = await api.post("/clients/bulk-assign", { client_ids: [...selected], sorumlu_personel: name });
      rememberStaff(data.sorumlu_personel);
      toast.success(`${data.count} mükellefin sorumlusu "${data.sorumlu_personel}" yapıldı`);
      setSelected(new Set()); setAssignTo(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Atama yapılamadı"); }
    finally { setAssigning(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/clients/${delTarget.id}`);
      toast.success("Mükellef pasife alındı");
      setDelTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Silme başarısız"); }
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-semibold tracking-tight">Mükellefler</h1>
          <p className="text-sm text-muted-foreground mt-1">{clients.length} kayıt</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="print-client-list-btn" variant="outline" onClick={printList}><Printer size={16} className="mr-1.5" /> Mükellef Listesi Yazdır</Button>
          <Button data-testid="add-client-btn" onClick={openNew}><Plus size={16} className="mr-1.5" /> Yeni Mükellef</Button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ünvan, VKN, telefon, yetkili ara..." className="pl-9" />
        </div>
        <Select value={tur} onValueChange={setTur}>
          <SelectTrigger className="w-44" data-testid="client-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hepsi">Tüm türler</SelectItem>
            {TURLER.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <Card data-testid="bulk-bar" className="p-3 flex flex-wrap items-center gap-3 border-sky-300 bg-sky-50 dark:bg-sky-500/10 sticky top-2 z-20">
          <span className="text-sm font-medium"><UserCheck size={15} className="inline mr-1.5 -mt-0.5" />{selected.size} mükellef seçili</span>
          <Input data-testid="bulk-staff-input" list="staff-names" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}
            placeholder="Sorumlu personel adı..." className="w-64 bg-background" />
          <datalist id="staff-names">{staffNames.map((n) => <option key={n} value={n} />)}</datalist>
          <Button data-testid="bulk-assign-btn" onClick={bulkAssign} disabled={assigning}>{assigning ? "Atanıyor..." : "Personel Ata"}</Button>
          <button className="ml-auto text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm" onClick={() => setSelected(new Set())}><X size={14} /> Seçimi temizle</button>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 w-10"><input data-testid="select-all-clients" type="checkbox" checked={allSelected} onChange={toggleAll} title="Listedeki tümünü seç" /></th>
                <th className="text-left font-medium px-4 py-3">Ünvan</th>
                <th className="text-left font-medium px-4 py-3">Tür</th>
                <th className="text-left font-medium px-4 py-3">VKN/TCKN</th>
                <th className="text-left font-medium px-4 py-3">Sorumlu</th>
                <th className="text-right font-medium px-4 py-3">Aylık Ücret</th>
                <th className="text-center font-medium px-4 py-3">Durum</th>
                <th className="text-center font-medium px-4 py-3 w-12">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => nav(`/mukellefler/${c.id}`)}
                  className={`border-t border-border hover:bg-accent cursor-pointer transition-colors ${selected.has(c.id) ? "bg-sky-50/70 dark:bg-sky-500/10" : ""}`}>
                  <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}><input data-testid={`select-client-${c.id}`} type="checkbox" checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} /></td>
                  <td className="px-4 py-3 font-medium flex items-center gap-2"><Building2 size={15} className="text-muted-foreground" />{c.unvan}</td>
                  <td className="px-4 py-3"><Badge variant="secondary">{c.sirket_turu}</Badge></td>
                  <td className="px-4 py-3 text-muted-foreground">{c.vkn || c.tckn}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.sorumlu_personel}</td>
                  <td className="px-4 py-3 text-right font-medium">{fmtTL(c.aylik_ucret)}</td>
                  <td className="px-4 py-3 text-center">{c.aktif ? <span className="text-emerald-600 text-xs">Aktif</span> : <span className="text-muted-foreground text-xs">Pasif</span>}</td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`client-actions-${c.id}`} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground"><MoreHorizontal size={16} /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`client-edit-${c.id}`} onClick={() => openEdit(c)}><Pencil size={14} className="mr-2" /> Düzenle</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`client-delete-${c.id}`} onClick={() => setDelTarget(c)} className="text-rose-600 focus:text-rose-600"><Trash2 size={14} className="mr-2" /> Sil</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Kayıt bulunamadı</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <ClientFormDialog open={open} onOpenChange={setOpen} client={editClient} onSaved={load} />

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mükellefi sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{delTarget?.unvan}</strong> pasife alınacaktır. Geçmiş kayıtları (beyanname, cari, görev) korunur. Devam etmek istiyor musunuz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-client" onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
